<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';

const route = useRoute();
const { accountScopedRoute } = useAccount();

const reportTabs = [
  {
    title: 'Visão Geral',
    items: [
      {
        label: 'Dashboard ao Vivo',
        icon: 'i-lucide-activity',
        routeName: 'account_overview_reports',
      },
      {
        label: 'Conversas',
        icon: 'i-lucide-message-square',
        routeName: 'conversation_reports',
      },
    ],
  },
  {
    title: 'Desempenho',
    items: [
      {
        label: 'Agentes',
        icon: 'i-lucide-user',
        routeName: 'agent_reports_index',
      },
      {
        label: 'Caixas de Entrada',
        icon: 'i-lucide-inbox',
        routeName: 'inbox_reports_index',
      },
      {
        label: 'Equipes',
        icon: 'i-lucide-users',
        routeName: 'team_reports_index',
      },
      {
        label: 'Etiquetas',
        icon: 'i-lucide-tags',
        routeName: 'label_reports_index',
      },
    ],
  },
  {
    title: 'Satisfação',
    items: [
      {
        label: 'CSAT',
        icon: 'i-lucide-smile',
        routeName: 'csat_reports',
      },
      {
        label: 'SLA',
        icon: 'i-lucide-timer',
        routeName: 'sla_reports',
      },
    ],
  },
  {
    title: 'Outros',
    items: [
      {
        label: 'Bot',
        icon: 'i-lucide-bot',
        routeName: 'bot_reports',
      },
      {
        label: 'CRM',
        icon: 'i-lucide-briefcase',
        routeName: 'crm_reports',
      },
    ],
  },
];

// Flatten all route names for matching
const allRouteNames = reportTabs.flatMap(s => s.items.map(i => i.routeName));

// Check if an item or its detail page is active
const isActive = routeName => {
  const current = route.name;
  if (current === routeName) return true;
  // Match detail pages (e.g. agent_reports_show matches agent_reports_index)
  if (current?.endsWith('_show') && routeName?.endsWith('_index')) {
    return current.replace('_show', '_index') === routeName;
  }
  return false;
};

// Only show nav on main report routes (not on detail/show pages that are too deep)
const showNav = computed(() => {
  const current = route.name;
  return (
    allRouteNames.includes(current) ||
    current?.endsWith('_show') ||
    // Old report routes
    ['agent_reports', 'inbox_reports', 'label_reports', 'team_reports'].includes(
      current
    )
  );
});
</script>

<template>
  <div class="reports--wrapper overflow-auto bg-n-surface-1 w-full">
    <!-- Report Navigation — full width, outside max-w container -->
    <nav
      v-if="showNav"
      class="reports-nav sticky top-0 z-10 flex items-center justify-center gap-0.5 px-4 py-1.5 border-b border-n-weak bg-n-surface-1"
    >
      <template v-for="(section, sIdx) in reportTabs" :key="section.title">
        <div
          v-if="sIdx > 0"
          class="w-px h-3.5 bg-n-stone-200 dark:bg-n-stone-400/20 mx-1 flex-shrink-0"
        />

        <RouterLink
          v-for="item in section.items"
          :key="item.routeName"
          :to="accountScopedRoute(item.routeName)"
          class="px-2 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap"
          :class="
            isActive(item.routeName)
              ? 'text-n-brand bg-n-brand/10'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2'
          "
        >
          {{ item.label }}
        </RouterLink>
      </template>
    </nav>

    <div class="max-w-7xl mx-auto pb-12 px-6">
      <router-view />
    </div>
  </div>
</template>

<style scoped lang="scss">
.reports--wrapper {
  :deep() {
    .multiselect--disabled {
      @apply opacity-50 border border-n-weak rounded-md cursor-not-allowed;
    }

    .multiselect__content-wrapper {
      @apply bg-n-solid-2 border border-n-weak text-n-slate-12;
    }

    .multiselect__tags {
      @apply bg-n-slate-1 border border-n-weak m-0 min-h-[2.875rem] pt-0;

      input[type='text'] {
        @apply bg-n-alpha-3 border-n-weak placeholder:text-n-slate-11 min-h-[2.375rem] h-[2.375rem] px-0.5 py-0 text-sm;
      }
    }

    .multiselect__placeholder {
      @apply text-n-slate-11;
    }

    .multiselect__select {
      @apply min-h-0;
    }

    .multiselect__single {
      @apply bg-n-alpha-3 text-n-slate-11;
    }

    .multiselect__input {
      @apply text-sm h-[2.375rem] mb-0 py-0;
    }

    .multiselect__tags,
    .multiselect__input,
    .multiselect {
      @apply bg-n-alpha-3 !border-n-weak text-n-slate-12 rounded-lg text-sm min-h-[2.5rem];
    }

    .mx-input-wrapper {
      @apply bg-n-alpha-3 border-n-weak text-n-slate-12 rounded-lg text-sm;

      input {
        @apply border-n-weak text-sm;
      }
    }

    .multiselect__option {
      @apply flex items-center;
    }

    .mx-datepicker {
      .mx-input {
        background-color: transparent !important;
      }

      .mx-input-wrapper input::placeholder {
        @apply text-n-slate-11;
      }

      .mx-input-wrapper input {
        @apply text-n-slate-11;
      }
    }

    .multiselect--active:not(.multiselect--above) .multiselect__current,
    .multiselect--active:not(.multiselect--above) .multiselect__input,
    .multiselect--active:not(.multiselect--above) .multiselect__tags {
      @apply rounded-b-none;
    }
  }
}
</style>
